import React, { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CreditCardIcon,
  InformationCircleIcon,
  PaintBrushIcon,
  TagIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/20/solid';
import { AddressAutofill } from '@mapbox/search-js-react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, Modifier } from 'draft-js';
import SingleDatePicker from '../../../components/ui/SingleDatePicker';
import TimeInput from '../../../components/ui/TimeInput';
import StyledMultiSelectDropdown from '../../dropdowns/StyledMultiSelectDropdown';
import ImageUploader from '../../uploads/ImageUploader';
import { Switch } from '../../../../components/ui/switch';
import { toast } from 'sonner';
import { FormColorSelector } from '../../../components/ui/form-color-selector';
import { COLORS } from '../../../constants/colors';
import { getCountryOptions } from '@charitystack/shared';
import { FormSelect } from '../../../components/ui/form-select';
import { FormTextarea } from '../../../components/ui/form-textarea';
import { SectionHeader } from '@/components/ui/section-header';
import { WorkspaceSectionCard } from '../../../../components/workspaces/EntityWorkspacePrimitives';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import { v4 as uuidv4 } from 'uuid';
import { getValidationToastMessage } from '../../../utils/validationToast';
import { getEventDateTimeRangeValidation } from '../../../utils/eventDateTimeValidation';
import {
  changeEventTicketInventory,
  changeEventTicketType,
  changeGroupSizeMode,
  getEventTicketConfigurationErrors,
  getEventTicketMinimumSubtotal,
} from '../../../utils/eventTicketAuthoring';

// Form steps for stepper
const FORM_STEPS = {
  APPEARANCE: 'appearance',
  TICKETS: 'ticket',
  PROMO_CODES: 'promo',
  CHECKOUT: 'checkout',
};

const getTicketConfigurationError = (ticket, field) =>
  getEventTicketConfigurationErrors(ticket).find((error) => error.field === field)?.message;

// ButtonGroup Component
const ButtonGroup = ({ currentStep, onBack, onContinue, isLastStep }) => {
  const getButtonText = () => {
    return 'Continue';
  };

  return (
    <div className="flex w-full justify-between">
      {/* Back Button - Show on all steps except first */}
      <div>
        {currentStep !== FORM_STEPS.APPEARANCE && (
          <Button variant="outline" onClick={onBack} className="h-[28px] gap-x-1 px-3 text-sm">
            <ArrowLeftIcon className="h-[14px] w-[14px] text-ink-muted" />
            <div>Back</div>
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Continue Button - No special handling for last step */}
        {!isLastStep && (
          <Button onClick={onContinue} className="h-[28px] gap-x-1 px-3 text-sm text-ink-muted">
            <div>{getButtonText()}</div>
            <ArrowRightIcon className="h-[14px] w-[14px] text-ink-muted" />
          </Button>
        )}
      </div>
    </div>
  );
};

function EventFormConfigurationTab(props) {
  const {
    formData,
    handleInputChange,
    editorState,
    setEditorState,
    merchantName,
    currentStep,
    setCurrentStep,
    displayColorPicker,
    setDisplayColorPicker,
    setInvalidFields,
    handleFieldFocus,
    isFieldInvalid,
    invalidFields,
    salesforceCampaignIDICNAValues,
    setSalesforceCampaignIDICNAValues,
    setSalesforceFieldMapperOpen,
    persistedTicketIDs = [],
    workspacePresentation = false,
  } = props;

  // Destructure formData for easier access
  const {
    formName,
    color,
    sketchPickerColor,
    eventStartDate,
    eventEndDate,
    eventStartTime,
    eventEndTime,
    eventLocation,
    timeZone,
    image,
    resizedImage,
    elementID,
    customTickets,
    customPromos,
    showAnonymousDonations,
    showOrganizationNameField,
    disableCheckoutBump,
    enableTimeAndLocation,
    enableEmbedForm,
    enablePhoneNumber,
    enableBillingAddress,
    selectedCountry,
    enableCommunicationConsent,
    communicationConsentMessage,
  } = formData;

  const countries = useMemo(() => getCountryOptions(), []); // frozen canonical options (PE-1878)
  // PE-1878 FE window: a stored country value ("Israel"/"Palestine, State of")
  // may no longer match a canonical option before the data backfill runs. Keep
  // any orphan value selectable so an existing form doesn't silently go blank
  // (and a re-save doesn't drop the country). Mirrors CreateEditContactDrawer.
  const countryLabels = useMemo(() => new Set(countries.map((c) => c.label)), [countries]);

  // Handler to strip HTML formatting when pasting into the editor
  const handlePastedText = (text, html, editorState) => {
    // Use DOMParser to properly strip HTML tags and decode entities
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const plainText = doc.body.textContent || '';

    // Replace selection with plain text (handles both collapsed cursor and highlighted text)
    // Note: Modifier.insertText requires a collapsed selection and throws if text is selected.
    // Modifier.replaceText works for both cases - it inserts at cursor when collapsed,
    // and replaces highlighted text when a range is selected.
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const newContent = Modifier.replaceText(currentContent, selection, plainText);
    const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');

    setEditorState(newEditorState);
    return 'handled';
  };

  // Address search handler for location input
  const handleAddressChange = (e) => {
    const { features } = e;
    if (features && features.length > 0) {
      const address = features[0].properties;

      // Format the complete address into a single string
      const addressComponents = [
        address.address_line1,
        address.address_line2,
        address.city || address.place,
        address.state_code || address.state || address.region_code,
        address.postcode,
        address.country,
      ].filter(Boolean); // Remove empty values

      const fullAddress = addressComponents.join(', ');
      handleInputChange('eventLocation', fullAddress);
    }
  };

  // Step validation with invalidFields and toast
  const validateCurrentStep = () => {
    const currentStepInvalidFields = new Set();
    const errorMessages = [];

    switch (currentStep) {
      case FORM_STEPS.APPEARANCE:
        if (!formName.trim()) {
          currentStepInvalidFields.add('form_configuration-appearance-formName');
          errorMessages.push('Event Name is required');
        }
        if (color.length !== 7) {
          currentStepInvalidFields.add('form_configuration-appearance-color');
          errorMessages.push('Color is required');
        }
        // Validate event details when enableTimeAndLocation is true
        if (enableTimeAndLocation) {
          if (!eventStartDate) {
            currentStepInvalidFields.add('form_configuration-appearance-eventStartDate');
            errorMessages.push('Event Start Date is required');
          }
          if (!eventStartTime || eventStartTime.trim() === '') {
            currentStepInvalidFields.add('form_configuration-appearance-eventStartTime');
            errorMessages.push('Event Start Time is required');
          }
          const dateTimeRangeValidation = getEventDateTimeRangeValidation({
            eventStartDate,
            eventEndDate,
            eventStartTime,
            eventEndTime,
          });
          if (dateTimeRangeValidation) {
            dateTimeRangeValidation.fields.forEach((field) =>
              currentStepInvalidFields.add(`form_configuration-appearance-${field}`),
            );
            errorMessages.push(dateTimeRangeValidation.message);
          }
        }
        break;

      case FORM_STEPS.TICKETS: {
        if (customTickets.length === 0) {
          errorMessages.push('At least one ticket must be created');
        }

        const seen_ticket_names = new Set();
        for (let i = 0; i < customTickets.length; i++) {
          var ticket = customTickets[i];
          if (seen_ticket_names.has(ticket.name)) {
            currentStepInvalidFields.add(`form_configuration-ticket-name-${i}`);
            errorMessages.push('Ticket names must be unique');
          } else {
            seen_ticket_names.add(ticket.name);
          }
          if (ticket.name === '') {
            currentStepInvalidFields.add(`form_configuration-ticket-name-${i}`);
            errorMessages.push('Ticket Name is required');
          }
          const validTicketTypes = ['SINGLE', 'GROUP'];
          if (!ticket.type || !validTicketTypes.includes(ticket.type)) {
            currentStepInvalidFields.add(`form_configuration-ticket-type-${i}`);
            errorMessages.push('Ticket Type is required');
          }
          getEventTicketConfigurationErrors(ticket).forEach(({ field, message }) => {
            currentStepInvalidFields.add(`form_configuration-ticket-${field}-${i}`);
            errorMessages.push(message);
          });

          if (ticket.price == null) {
            currentStepInvalidFields.add(`form_configuration-ticket-price-${i}`);
            errorMessages.push('Ticket price is required');
          }
          if (!Number.isSafeInteger(Number(ticket.quantity)) || Number(ticket.quantity) <= 0) {
            currentStepInvalidFields.add(`form_configuration-ticket-quantity-${i}`);
            errorMessages.push('Quantity must be a positive whole number');
          }
        }
        break;
      }

      case FORM_STEPS.PROMO_CODES:
        // Promo codes validation (optional section)
        for (let i = 0; i < customPromos.length; i++) {
          var promo = customPromos[i];
          if (promo.codeName === '') {
            currentStepInvalidFields.add(`form_configuration-promo-codeName-${i}`);
            errorMessages.push('Promo Code Name is required');
          }
          if (promo.codeType === 'Select an promo code Type') {
            currentStepInvalidFields.add(`form_configuration-promo-codeType-${i}`);
            errorMessages.push('Promo Code Type is required');
          }

          if (promo.codeAmount == null || isNaN(promo.codeAmount)) {
            currentStepInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
            errorMessages.push('Promo Code Amount is required');
          }
          if (promo.codeType === 'AMOUNT' && promo.codeAmount == 0) {
            currentStepInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
            errorMessages.push('Promo Code Amount cannot be 0');
          }

          // Check if promo amount exceeds max allowed for AMOUNT type
          if (promo.codeType === 'AMOUNT' && promo.codeAmount > 0) {
            const minPrice = getMinimumTicketPrice(promo);
            if (minPrice !== null && promo.codeAmount > minPrice) {
              currentStepInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
              errorMessages.push(
                `Promo code "${promo.codeName}" discount cannot exceed $${minPrice.toFixed(2)}`,
              );
            }
          }

          if (promo.tickets.length === 0) {
            currentStepInvalidFields.add(`form_configuration-promo-tickets-${i}`);
            errorMessages.push('Tickets are required for each promo');
          }

          if (promo.codeAmount > 100 && promo.codeType === 'PERCENTAGE') {
            currentStepInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
            errorMessages.push('Promo Code Amount cannot be more than 100%');
          }
          if (promo.codeAmount < 0 && promo.codeType === 'PERCENTAGE') {
            currentStepInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
            errorMessages.push('Promo Code Amount cannot be less than 0');
          }
        }
        break;

      case FORM_STEPS.CHECKOUT:
        // Checkout details validation (if any)
        break;

      default:
        break;
    }

    // Get current step field prefixes to identify which fields belong to this step
    const getCurrentStepFieldPrefixes = () => {
      switch (currentStep) {
        case FORM_STEPS.APPEARANCE:
          return ['form_configuration-appearance-'];
        // return [
        //   "form_configuration-appearance-formName",
        //   "form_configuration-appearance-color",
        // ];
        case FORM_STEPS.TICKETS:
          return ['form_configuration-ticket-'];
        case FORM_STEPS.PROMO_CODES:
          return ['form_configuration-promo-'];
        case FORM_STEPS.CHECKOUT:
          return ['form_configuration-checkout-'];
        default:
          return [];
      }
    };

    // Update invalid fields state by merging with existing errors from other tabs
    setInvalidFields((prevInvalidFields) => {
      const newInvalidFields = new Set(prevInvalidFields);
      const currentStepPrefixes = getCurrentStepFieldPrefixes();

      // Remove existing errors for the current step
      for (const field of newInvalidFields) {
        const shouldRemove = currentStepPrefixes.some(
          (prefix) => field === prefix || field.startsWith(prefix),
        );
        if (shouldRemove) {
          newInvalidFields.delete(field);
        }
      }

      // Add new errors for the current step
      currentStepInvalidFields.forEach((field) => {
        newInvalidFields.add(field);
      });

      return newInvalidFields;
    });

    // Show toast with errors if any
    if (errorMessages.length > 0) {
      toast.error(getValidationToastMessage(errorMessages, 'Please fix the validation errors'));
      return false;
    }

    return true;
  };

  // Step navigation
  const handleContinue = () => {
    if (!validateCurrentStep()) {
      return;
    }

    const steps = Object.values(FORM_STEPS);
    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
    // Remove the save form call - now handled by Publish button
  };

  const handleBack = () => {
    const steps = Object.values(FORM_STEPS);
    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const isLastStep = () => {
    const steps = Object.values(FORM_STEPS);
    return currentStep === steps[steps.length - 1];
  };

  // Ticket handler functions
  const handleTicketNameChange = (event) => {
    let index = parseInt(event.target.dataset.index);
    let value = event.target.value;

    let newCustomTickets = [...customTickets];
    let newCustomTicket = newCustomTickets[index];
    newCustomTicket['name'] = value;
    handleInputChange('customTickets', newCustomTickets);
  };

  const handleTicketTypeChange = (index, value) => {
    let newCustomTickets = [...customTickets];
    newCustomTickets[index] = changeEventTicketType(newCustomTickets[index], value);
    handleInputChange('customTickets', newCustomTickets);
  };

  const updateTicketField = (index, field, value) => {
    const newCustomTickets = [...customTickets];
    newCustomTickets[index] = { ...newCustomTickets[index], [field]: value };
    handleInputChange('customTickets', newCustomTickets);
  };

  const handleGroupSizeModeChange = (index, value) => {
    const newCustomTickets = [...customTickets];
    newCustomTickets[index] = changeGroupSizeMode(newCustomTickets[index], value);
    handleInputChange('customTickets', newCustomTickets);
  };

  const handleTicketPriceChange = (event) => {
    let index = parseInt(event.target.dataset.index);
    let value = event.target.value;

    // Allow empty string
    if (value === '') {
      let newCustomTickets = [...customTickets];
      newCustomTickets[index]['price'] = 0;
      handleInputChange('customTickets', newCustomTickets);
      return;
    }

    // Only allow numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    // checking if value include a dec point
    if (value.match(/\./g)) {
      const [, decimal] = value.split('.');

      // restrict value to only 2 decimal places
      if (decimal?.length > 2) {
        return;
      }
    }

    let newCustomTickets = [...customTickets];
    let newCustomTicket = newCustomTickets[index];
    // Store the raw string value to preserve decimal point and trailing zeros during input
    // This allows users to type "45." or "45.5" without the decimal being stripped
    newCustomTicket['price'] = value;
    const newPrice = parseFloat(value) || 0;

    // If ticket becomes free (price = 0), remove it from any promos that include it
    if (newPrice === 0 && customPromos.length > 0) {
      const ticketId = newCustomTicket.id;
      const ticketName = newCustomTicket.name;

      let promosUpdated = false;
      const newPromos = customPromos.map((promo) => {
        const hasTicket =
          promo.tickets?.some((t) => t.id === ticketId || t.name === ticketName) ||
          promo.tickets2?.some((t) => t.id === ticketId || t.name === ticketName);

        if (hasTicket) {
          promosUpdated = true;
          return {
            ...promo,
            tickets: promo.tickets?.filter((t) => t.id !== ticketId && t.name !== ticketName) || [],
            tickets2:
              promo.tickets2?.filter((t) => t.id !== ticketId && t.name !== ticketName) || [],
          };
        }
        return promo;
      });

      if (promosUpdated) {
        handleInputChange('customPromos', newPromos);
        toast.info(
          `Free tickets cannot be applied to promo codes. Ticket "${ticketName}" has been removed from applicable promos.`,
        );
      }
    }

    handleInputChange('customTickets', newCustomTickets);
  };

  const handleTicketFairMarketValueChange = (event) => {
    let index = parseInt(event.target.dataset.index);
    let value = event.target.value;

    // checking if value include a dec point
    if (value.match(/\./g)) {
      const [, decimal] = value.split('.');

      // restrict value to only 2 decimal places
      if (decimal?.length > 2) {
        return;
      }
    }

    let newCustomTickets = [...customTickets];
    let newCustomTicket = newCustomTickets[index];
    newCustomTicket['fairMarketValue'] = parseFloat(value) || 0;
    handleInputChange('customTickets', newCustomTickets);
  };

  const handleTicketQuantityChange = (event) => {
    const index = parseInt(event.target.dataset.index);
    const newCustomTickets = [...customTickets];
    newCustomTickets[index] = changeEventTicketInventory(
      newCustomTickets[index],
      'quantity',
      event.target.value,
    );
    handleInputChange('customTickets', newCustomTickets);
  };

  const handleTicketSeatsChange = (event) => {
    const index = parseInt(event.target.dataset.index);
    const newCustomTickets = [...customTickets];
    newCustomTickets[index] = changeEventTicketInventory(
      newCustomTickets[index],
      'seats',
      event.target.value,
    );
    handleInputChange('customTickets', newCustomTickets);
  };

  const addNewTicket = () => {
    let newCustomTickets = [...customTickets];
    newCustomTickets.push({
      id: uuidv4(),
      name: '',
      price: 0,
      fairMarketValue: 0,
      quantity: 0,
      type: 'SINGLE',
      seats: 2,
      sold: 0,
      attendees: [],
    });
    handleInputChange('customTickets', newCustomTickets);
  };

  const removeTicket = (index) => {
    let newCustomTickets = [...customTickets];
    newCustomTickets.splice(index, 1);
    handleInputChange('customTickets', newCustomTickets);
  };

  // Promo code handler functions
  const handlePromoNameChange = (event) => {
    let index = parseInt(event.target.dataset.index);
    let value = event.target.value;

    let newPromos = [...customPromos];
    let newPromo = newPromos[index];
    newPromo['codeName'] = value;
    handleInputChange('customPromos', newPromos);
  };

  const handlePromoTypeChange = (index, value) => {
    let newPromos = [...customPromos];
    let newPromo = newPromos[index];
    newPromo['codeType'] = value;
    handleInputChange('customPromos', newPromos);
  };

  const handlePromoAmountChange = (event) => {
    let index = parseInt(event.target.dataset.index);
    let value = event.target.value;

    // checking if value include a dec point
    if (value.match(/\./g)) {
      const [, decimal] = value.split('.');

      // restrict value to only 2 decimal places
      if (decimal?.length > 2) {
        return;
      }
    }

    let newPromos = [...customPromos];
    let newPromo = newPromos[index];
    newPromo['codeAmount'] = parseFloat(value) || 0;
    handleInputChange('customPromos', newPromos);
  };

  const handlePromoTicketsChange = (selectedTickets, index) => {
    let value = getSelectedTickets(selectedTickets);

    let newPromos = [...customPromos];
    let newPromo = newPromos[index];
    newPromo['tickets'] = value;
    newPromo['tickets2'] = value;

    handleInputChange('customPromos', newPromos);
  };

  // Given a list of selected ticket names, return a list of maps with ticket id and name
  function getSelectedTickets(selectedTickets) {
    let selectedTicketsList = [];
    selectedTickets.forEach((ticketName) => {
      let ticket = customTickets.find((t) => t.name === ticketName);
      if (ticket) {
        selectedTicketsList.push({
          id: ticket.id,
          name: ticket.name,
        });
      }
    });
    return selectedTicketsList;
  }

  const addNewPromo = () => {
    if (customTickets.length === 0) {
      toast.error('A ticket needs to be created first');
      return;
    }

    let newPromos = [...customPromos];
    newPromos.push({
      codeId: uuidv4(),
      codeName: '',
      codeType: 'PERCENTAGE',
      codeAmount: 0,
      codeQuantity: 1,
      tickets: [],
      tickets2: [],
    });
    handleInputChange('customPromos', newPromos);
  };

  const removePromo = (index) => {
    let newPromos = [...customPromos];
    newPromos.splice(index, 1);
    handleInputChange('customPromos', newPromos);
  };

  // Helper function to check if a step has validation errors
  const getStepValidationErrors = (stepKey) => {
    // Check if any field in invalidFields contains this step's identifier
    const stepIdentifier = `form_configuration-${stepKey}-`;
    return Array.from(invalidFields).some((field) => field.startsWith(stepIdentifier));
  };

  // Helper function to get the minimum ticket price for promo's applied tickets
  const getMinimumTicketPrice = (promo) => {
    if (!promo.tickets || promo.tickets.length === 0) {
      return null;
    }

    const associatedTickets = promo.tickets
      .map((promoTicket) => {
        return customTickets.find((ticket) => ticket.id === promoTicket.id);
      })
      .filter(Boolean);

    if (associatedTickets.length === 0) {
      return null;
    }

    const minPrice = Math.min(
      ...associatedTickets.map((ticket) => getEventTicketMinimumSubtotal(ticket)),
    );
    return minPrice;
  };

  const renderAllFormSteps = () => {
    const steps = [
      {
        key: FORM_STEPS.APPEARANCE,
        title: 'Appearance',
        content: (
          <div className="space-y-4">
            {/* Title Input */}
            <div>
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Title
              </Label>
              <Input
                type="text"
                value={formName}
                onChange={(e) => handleInputChange('formName', e.target.value.replace('|', ''))}
                onFocus={() => handleFieldFocus('form_configuration-appearance-formName')}
                className={`h-7 ${
                  isFieldInvalid('form_configuration-appearance-formName') ? 'border-red-500' : ''
                }`}
              />
            </div>

            {/* Description */}
            <div>
              <Label className="mb-2 block font-inter text-sm font-medium leading-5 text-ink-muted">
                Description
              </Label>
              <Editor
                editorStyle={{
                  border: 1,
                  borderRadius: 5,
                  paddingHorizontal: 4,
                  minHeight: 200,
                  paddingVertical: 2,
                }}
                toolbarStyle={{
                  borderRadius: 5,
                  backgroundColor: '#F0ECE4',
                }}
                wrapperStyle={{ borderRadius: 5 }}
                editorState={editorState}
                onEditorStateChange={setEditorState}
                handlePastedText={handlePastedText}
                wrapperClassName="wrapper-class border h-100 border-edge"
                editorClassName="editor-class h-100 px-4 py-0"
                toolbarClassName="toolbar-class"
                toolbar={{
                  options: ['link', 'list'],
                  list: {
                    inDropdown: false,
                    options: ['unordered', 'ordered'],
                  },
                  link: {
                    inDropdown: false,
                    showOpenOptionOnHover: true,
                    defaultTargetOption: '_blank',
                  },
                }}
              />
              {editorState && (
                <p
                  className={`text-xs text-right mt-1 ${
                    editorState.getCurrentContent().getPlainText('').length > 2500
                      ? 'text-red-500 font-medium'
                      : 'text-ink-muted'
                  }`}
                >
                  {editorState.getCurrentContent().getPlainText('').length}/2500 characters
                </p>
              )}
            </div>

            {/* Image Uploader */}
            <div>
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Header Image
              </Label>
              <ImageUploader
                merchantName={merchantName}
                elementID={elementID}
                image_type={'event'}
                desiredHeight={0}
                image={image}
                setImage={(value) => handleInputChange('image', value)}
                resizedImage={resizedImage}
                setResizedImage={(value) => handleInputChange('resizedImage', value)}
                desiredWidth={0}
              />
            </div>

            {/* Color Picker */}
            <div>
              <Label className="mb-2 block font-inter text-sm font-medium leading-5 text-ink-muted">
                Color
              </Label>
              <FormColorSelector
                value={color}
                onChange={(value) => handleInputChange('color', value)}
                defaultColor={COLORS.FORM_DEFAULT_COLOR}
                className="h-7"
                isInvalid={isFieldInvalid('form_configuration-appearance-color')}
                onFocus={() => handleFieldFocus('form_configuration-appearance-color')}
              />
            </div>

            {/* Elements */}
            <div className="pt-4">
              <Label className="text-base font-medium text-ink-secondary">
                Elements Customization
              </Label>

              {/* Details Page */}
              <div className="py-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={enableEmbedForm}
                    onCheckedChange={(value) => handleInputChange('enableEmbedForm', value)}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Details Page
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Shows description, FAQs, pictures, and more
                    </p>
                  </div>
                </div>
              </div>

              {/* Event Time and Location */}
              <div className="border-t border-edge py-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={enableTimeAndLocation}
                    onCheckedChange={(value) => handleInputChange('enableTimeAndLocation', value)}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Event Time and Location
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Show date, time, and venue on the event form
                    </p>
                  </div>
                </div>

                {enableTimeAndLocation && (
                  <div className="mt-2 space-y-3 rounded-md border border-edge bg-paper-light p-3">
                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-3">
                      <SingleDatePicker
                        label="Start Date"
                        value={eventStartDate}
                        onChange={(e) => {
                          handleInputChange('eventStartDate', e);
                          // Clear end date when start date is cleared
                          if (!e) {
                            handleInputChange('eventEndDate', null);
                          }
                        }}
                        placeholder="Select start date"
                        disablePastDates={true}
                        required={false}
                        isInvalid={isFieldInvalid('form_configuration-appearance-eventStartDate')}
                        onFocus={() =>
                          handleFieldFocus('form_configuration-appearance-eventStartDate')
                        }
                      />
                      <SingleDatePicker
                        label="End Date"
                        value={eventEndDate}
                        onChange={(e) => handleInputChange('eventEndDate', e)}
                        placeholder="Select end date"
                        disablePastDates={true}
                        required={false}
                        minDate={eventStartDate}
                        disabled={!eventStartDate}
                        isInvalid={isFieldInvalid('form_configuration-appearance-eventEndDate')}
                        onFocus={() =>
                          handleFieldFocus('form_configuration-appearance-eventEndDate')
                        }
                      />
                    </div>

                    {/* Time Range */}
                    <div className="grid grid-cols-2 gap-3">
                      <TimeInput
                        label="Start Time"
                        value={eventStartTime}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          handleInputChange('eventStartTime', newValue);
                          // Clear end time when start time is cleared
                          if (!newValue) {
                            handleInputChange('eventEndTime', '');
                          }
                        }}
                        minuteStep={15}
                        isInvalid={isFieldInvalid('form_configuration-appearance-eventStartTime')}
                        onFocus={() =>
                          handleFieldFocus('form_configuration-appearance-eventStartTime')
                        }
                      />
                      <TimeInput
                        label="End Time"
                        value={eventEndTime}
                        onChange={(e) => handleInputChange('eventEndTime', e.target.value)}
                        disabled={!eventStartTime}
                        minuteStep={15}
                      />
                    </div>

                    {/* Timezone */}
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        Timezone
                      </Label>
                      <TimezoneSelect
                        value={timeZone}
                        onChange={(value) => handleInputChange('timeZone', value)}
                      />
                    </div>

                    {/* Location */}
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        Location
                      </Label>
                      <AddressAutofill
                        accessToken={import.meta.env.VITE_APP_SCOPED_MAPBOX_TOKEN}
                        onRetrieve={handleAddressChange}
                      >
                        <Input
                          type="text"
                          value={eventLocation}
                          onChange={(e) => handleInputChange('eventLocation', e.target.value)}
                          className="h-7"
                          placeholder="Enter event location"
                          autoComplete="address-line1"
                        />
                      </AddressAutofill>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: FORM_STEPS.TICKETS,
        title: 'Tickets',
        content: (
          <div className="flex flex-col gap-4">
            {customTickets.map((ticket, index) => (
              <div key={ticket.id} className="rounded-md border-edge">
                {/* Ticket Name */}
                <div className="flex items-center justify-between">
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Ticket #{index + 1}
                  </Label>
                </div>
                <div className="space-y-3 rounded-md bg-paper-inset p-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        Ticket Name
                      </Label>
                      {customTickets.length > 1 && (
                        <button
                          onClick={() => removeTicket(index)}
                          className="pb-1 pr-0 text-ink-muted hover:text-ink-secondary"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 6L6 18" />
                            <path d="M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <Input
                      type="text"
                      value={ticket.name}
                      data-index={index}
                      onChange={handleTicketNameChange}
                      onFocus={() => handleFieldFocus(`form_configuration-ticket-name-${index}`)}
                      className={`h-7 ${
                        isFieldInvalid(`form_configuration-ticket-name-${index}`)
                          ? 'border-red-500'
                          : ''
                      }`}
                      maxLength={50}
                    />
                  </div>

                  {/* Price and individual ticket quantity */}
                  <div className={`grid gap-3 ${ticket.type === 'GROUP' ? '' : 'grid-cols-2'}`}>
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        Price
                      </Label>
                      <div className="relative">
                        {/* Left $ label */}
                        <span className="absolute left-[10px] top-1/2 -translate-y-1/2 text-sm leading-none text-ink-muted">
                          $
                        </span>

                        {/* Input field */}
                        <input
                          type="text"
                          value={ticket.price || ''}
                          data-index={index}
                          onChange={handleTicketPriceChange}
                          onFocus={() =>
                            handleFieldFocus(`form_configuration-ticket-price-${index}`)
                          }
                          onWheel={(e) => e.target.blur()}
                          className={`block h-7 w-full rounded-md bg-white border border-edge-input pl-5 pr-12 text-sm leading-none focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage ${
                            isFieldInvalid(`form_configuration-ticket-price-${index}`)
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                              : ''
                          }`}
                          placeholder="0.00"
                        />

                        {/* Right USD label */}
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-muted">
                          USD
                        </span>
                      </div>
                    </div>
                    {ticket.type !== 'GROUP' && (
                      <div>
                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                          Quantity
                        </Label>
                        <Input
                          id={`form_configuration-ticket-quantity-${index}`}
                          name={`form_configuration-ticket-quantity-${index}`}
                          type="number"
                          min="1"
                          value={ticket.quantity || ''}
                          data-index={index}
                          onChange={handleTicketQuantityChange}
                          onFocus={() =>
                            handleFieldFocus(`form_configuration-ticket-quantity-${index}`)
                          }
                          onWheel={(e) => e.target.blur()}
                          className={`h-7 ${
                            isFieldInvalid(`form_configuration-ticket-quantity-${index}`)
                              ? 'border-red-500'
                              : ''
                          }`}
                          placeholder="1"
                        />
                      </div>
                    )}
                  </div>

                  {/* Ticket Type */}
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Ticket Type
                    </Label>
                    <div className="flex space-x-4 pt-2">
                      <button
                        type="button"
                        aria-pressed={ticket.type === 'SINGLE'}
                        disabled={persistedTicketIDs.includes(ticket.id)}
                        className="flex flex-1 items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => {
                          handleFieldFocus(`form_configuration-ticket-type-${index}`);
                          handleTicketTypeChange(index, 'SINGLE');
                        }}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                            isFieldInvalid(`form_configuration-ticket-type-${index}`)
                              ? 'border-red-500'
                              : ticket.type === 'SINGLE'
                              ? 'border-sage'
                              : 'border-edge'
                          }`}
                          aria-hidden
                        >
                          {ticket.type === 'SINGLE' && (
                            <span className="h-2 w-2 rounded-full bg-sage" />
                          )}
                        </span>
                        <span className="text-sm text-ink-secondary">Individual</span>
                      </button>
                      <button
                        type="button"
                        aria-pressed={ticket.type === 'GROUP'}
                        disabled={persistedTicketIDs.includes(ticket.id)}
                        className="flex flex-1 items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => {
                          handleFieldFocus(`form_configuration-ticket-type-${index}`);
                          handleTicketTypeChange(index, 'GROUP');
                        }}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                            isFieldInvalid(`form_configuration-ticket-type-${index}`)
                              ? 'border-red-500'
                              : ticket.type === 'GROUP'
                              ? 'border-sage'
                              : 'border-edge'
                          }`}
                          aria-hidden
                        >
                          {ticket.type === 'GROUP' && (
                            <span className="h-2 w-2 rounded-full bg-sage" />
                          )}
                        </span>
                        <span className="text-sm text-ink-secondary">Group</span>
                      </button>
                    </div>
                    {persistedTicketIDs.includes(ticket.id) && (
                      <p className="mt-1 text-xs text-ink-muted">
                        Ticket type cannot be changed after the event is created.
                      </p>
                    )}
                  </div>

                  {/* Group Ticket configuration */}
                  {ticket.type === 'GROUP' && (
                    <div className="space-y-3 rounded-md border border-edge bg-paper-light p-3">
                      {ticket.ticketSalesPaused === true && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                          Sales are paused because a refund left this ticket over its configured
                          inventory. You can increase inventory here; clearing the incident remains
                          an operational remediation step.
                        </div>
                      )}
                      <fieldset>
                        <legend className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                          Seat selection
                        </legend>
                        <div className="mt-1 flex rounded-md border border-edge bg-paper-inset p-0.5">
                          {[
                            ['FIXED', 'Fixed seats'],
                            ['MINIMUM', 'Flexible seats'],
                          ].map(([value, label]) => (
                            <label key={value} className="relative flex-1 cursor-pointer">
                              <input
                                type="radio"
                                name={`form_configuration-ticket-groupSizeMode-${index}`}
                                value={value}
                                checked={(ticket.groupSizeMode ?? 'FIXED') === value}
                                onChange={() => {
                                  handleFieldFocus(
                                    `form_configuration-ticket-groupSizeMode-${index}`,
                                  );
                                  handleGroupSizeModeChange(index, value);
                                }}
                                className="peer sr-only"
                              />
                              <span
                                className={`flex min-h-8 items-center justify-center rounded-[4px] border px-2 text-sm font-medium transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-sage peer-focus-visible:ring-offset-1 ${
                                  (ticket.groupSizeMode ?? 'FIXED') === value
                                    ? 'border-edge bg-paper-light text-ink-secondary'
                                    : 'border-transparent text-ink-muted hover:text-ink-secondary'
                                }`}
                              >
                                {label}
                              </span>
                            </label>
                          ))}
                        </div>
                        <p className="mt-1 text-xs text-ink-muted">
                          {(ticket.groupSizeMode ?? 'FIXED') === 'FIXED'
                            ? 'Every order includes the same number of seats.'
                            : 'Set a minimum and optional maximum. Buyers choose their seat count.'}
                        </p>
                      </fieldset>

                      <fieldset>
                        <legend className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                          Pricing
                        </legend>
                        <div className="mt-1 flex rounded-md border border-edge bg-paper-inset p-0.5">
                          {[
                            ['FLAT', 'Flat group price'],
                            ['PER_SEAT', 'Price per seat'],
                          ].map(([value, label]) => (
                            <label key={value} className="relative flex-1 cursor-pointer">
                              <input
                                type="radio"
                                name={`form_configuration-ticket-pricingMode-${index}`}
                                value={value}
                                checked={(ticket.pricingMode ?? 'FLAT') === value}
                                onChange={() => {
                                  handleFieldFocus(
                                    `form_configuration-ticket-pricingMode-${index}`,
                                  );
                                  updateTicketField(index, 'pricingMode', value);
                                }}
                                className="peer sr-only"
                              />
                              <span
                                className={`flex min-h-8 items-center justify-center rounded-[4px] border px-2 text-sm font-medium transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-sage peer-focus-visible:ring-offset-1 ${
                                  (ticket.pricingMode ?? 'FLAT') === value
                                    ? 'border-edge bg-paper-light text-ink-secondary'
                                    : 'border-transparent text-ink-muted hover:text-ink-secondary'
                                }`}
                              >
                                {label}
                              </span>
                            </label>
                          ))}
                        </div>
                        <p className="mt-1 text-xs text-ink-muted">
                          {ticket.pricingMode === 'PER_SEAT'
                            ? 'The ticket price above applies to each selected seat.'
                            : 'The ticket price above applies once to the whole group.'}
                        </p>
                      </fieldset>

                      {(ticket.groupSizeMode ?? 'FIXED') === 'FIXED' ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <Label
                              htmlFor={`form_configuration-ticket-seats-${index}`}
                              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                            >
                              Seats per group
                            </Label>
                            <Input
                              id={`form_configuration-ticket-seats-${index}`}
                              name={`form_configuration-ticket-seats-${index}`}
                              type="number"
                              min={2}
                              value={ticket.seats || ''}
                              data-index={index}
                              onChange={handleTicketSeatsChange}
                              onFocus={() =>
                                handleFieldFocus(`form_configuration-ticket-seats-${index}`)
                              }
                              onWheel={(e) => e.target.blur()}
                              className={`h-7 w-full ${
                                isFieldInvalid(`form_configuration-ticket-seats-${index}`)
                                  ? 'border-red-500'
                                  : ''
                              }`}
                              placeholder="2"
                            />
                          </div>
                          <div>
                            <Label
                              htmlFor={`form_configuration-ticket-quantity-${index}`}
                              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                            >
                              Group inventory
                            </Label>
                            <Input
                              id={`form_configuration-ticket-quantity-${index}`}
                              name={`form_configuration-ticket-quantity-${index}`}
                              type="number"
                              min="1"
                              value={ticket.quantity || ''}
                              data-index={index}
                              onChange={handleTicketQuantityChange}
                              onFocus={() =>
                                handleFieldFocus(`form_configuration-ticket-quantity-${index}`)
                              }
                              onWheel={(e) => e.target.blur()}
                              className={`h-7 ${
                                isFieldInvalid(`form_configuration-ticket-quantity-${index}`)
                                  ? 'border-red-500'
                                  : ''
                              }`}
                              placeholder="1"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-xs text-ink-muted">
                              Total seat inventory:{' '}
                              <span className="font-medium text-ink-secondary">
                                {Number(ticket.quantity || 0) * Number(ticket.seats || 0)} seats
                              </span>{' '}
                              across {ticket.quantity || 0} groups.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <Label
                              htmlFor={`form_configuration-ticket-minimumSeats-${index}`}
                              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                            >
                              Minimum seats
                            </Label>
                            <Input
                              id={`form_configuration-ticket-minimumSeats-${index}`}
                              type="number"
                              min={2}
                              value={ticket.minimumSeats ?? ''}
                              onChange={(event) =>
                                updateTicketField(
                                  index,
                                  'minimumSeats',
                                  event.target.value === '' ? '' : Number(event.target.value),
                                )
                              }
                              onFocus={() =>
                                handleFieldFocus(`form_configuration-ticket-minimumSeats-${index}`)
                              }
                              aria-invalid={Boolean(
                                getTicketConfigurationError(ticket, 'minimumSeats') ||
                                  isFieldInvalid(`form_configuration-ticket-minimumSeats-${index}`),
                              )}
                              className={`h-7 ${
                                getTicketConfigurationError(ticket, 'minimumSeats') ||
                                isFieldInvalid(`form_configuration-ticket-minimumSeats-${index}`)
                                  ? 'border-red-500'
                                  : ''
                              }`}
                            />
                            {getTicketConfigurationError(ticket, 'minimumSeats') ? (
                              <p className="mt-1 text-xs text-red-600" role="alert">
                                {getTicketConfigurationError(ticket, 'minimumSeats')}
                              </p>
                            ) : null}
                          </div>
                          <div>
                            <Label
                              htmlFor={`form_configuration-ticket-maximumSeats-${index}`}
                              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                            >
                              Maximum seats
                            </Label>
                            <Input
                              id={`form_configuration-ticket-maximumSeats-${index}`}
                              type="number"
                              min={ticket.minimumSeats || 2}
                              value={ticket.maximumSeats ?? ''}
                              placeholder="No maximum"
                              onChange={(event) =>
                                updateTicketField(
                                  index,
                                  'maximumSeats',
                                  event.target.value === '' ? null : Number(event.target.value),
                                )
                              }
                              onFocus={() =>
                                handleFieldFocus(`form_configuration-ticket-maximumSeats-${index}`)
                              }
                              aria-invalid={Boolean(
                                getTicketConfigurationError(ticket, 'maximumSeats') ||
                                  isFieldInvalid(`form_configuration-ticket-maximumSeats-${index}`),
                              )}
                              className={`h-7 ${
                                getTicketConfigurationError(ticket, 'maximumSeats') ||
                                isFieldInvalid(`form_configuration-ticket-maximumSeats-${index}`)
                                  ? 'border-red-500'
                                  : ''
                              }`}
                            />
                            {getTicketConfigurationError(ticket, 'maximumSeats') ? (
                              <p className="mt-1 text-xs text-red-600" role="alert">
                                {getTicketConfigurationError(ticket, 'maximumSeats')}
                              </p>
                            ) : (
                              <p className="mt-1 text-xs text-ink-muted">Optional</p>
                            )}
                          </div>
                          <div>
                            <Label
                              htmlFor={`form_configuration-ticket-quantity-${index}`}
                              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                            >
                              Group inventory
                            </Label>
                            <Input
                              id={`form_configuration-ticket-quantity-${index}`}
                              name={`form_configuration-ticket-quantity-${index}`}
                              type="number"
                              min="1"
                              value={ticket.quantity || ''}
                              data-index={index}
                              onChange={handleTicketQuantityChange}
                              onFocus={() =>
                                handleFieldFocus(`form_configuration-ticket-quantity-${index}`)
                              }
                              onWheel={(e) => e.target.blur()}
                              className={`h-7 ${
                                isFieldInvalid(`form_configuration-ticket-quantity-${index}`)
                                  ? 'border-red-500'
                                  : ''
                              }`}
                              placeholder="1"
                            />
                          </div>
                          <div>
                            <Label
                              htmlFor={`form_configuration-ticket-seatCapacity-${index}`}
                              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                            >
                              Seat inventory
                            </Label>
                            <Input
                              id={`form_configuration-ticket-seatCapacity-${index}`}
                              type="number"
                              min={ticket.minimumSeats || 2}
                              value={ticket.seatCapacity ?? ''}
                              onChange={(event) =>
                                updateTicketField(
                                  index,
                                  'seatCapacity',
                                  event.target.value === '' ? '' : Number(event.target.value),
                                )
                              }
                              onFocus={() =>
                                handleFieldFocus(`form_configuration-ticket-seatCapacity-${index}`)
                              }
                              aria-invalid={Boolean(
                                getTicketConfigurationError(ticket, 'seatCapacity') ||
                                  isFieldInvalid(`form_configuration-ticket-seatCapacity-${index}`),
                              )}
                              className={`h-7 ${
                                getTicketConfigurationError(ticket, 'seatCapacity') ||
                                isFieldInvalid(`form_configuration-ticket-seatCapacity-${index}`)
                                  ? 'border-red-500'
                                  : ''
                              }`}
                            />
                            {getTicketConfigurationError(ticket, 'seatCapacity') ? (
                              <p className="mt-1 text-xs text-red-600" role="alert">
                                {getTicketConfigurationError(ticket, 'seatCapacity')}
                              </p>
                            ) : (
                              <p className="mt-1 text-xs text-ink-muted">
                                Across all {ticket.quantity || 0} groups
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {(Number(ticket.sold || 0) > 0 || Number(ticket.seatsSold || 0) > 0) && (
                        <div className="flex items-start gap-2 rounded-md bg-paper-muted px-3 py-2 text-xs text-ink-muted">
                          <InformationCircleIcon
                            className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted"
                            aria-hidden="true"
                          />
                          <p>
                            Changes apply to future orders only. Existing orders keep their original
                            seat counts and prices.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Add New Ticket Button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={addNewTicket}
                className="flex items-center space-x-1 text-ink-secondary hover:text-ink"
              >
                <PlusIcon className="h-4 w-4" />
                <span className="text-sm">Add ticket</span>
              </button>
            </div>
          </div>
        ),
      },
      {
        key: FORM_STEPS.PROMO_CODES,
        title: 'Promo Codes (Optional)',
        content: (
          <div className="flex flex-col gap-4">
            {customPromos.map((promo, index) => (
              <div key={promo.codeId} className="rounded-md border-edge">
                <div className="flex items-center justify-between">
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Promo Code #{index + 1}
                  </Label>
                </div>
                <div className="space-y-3 rounded-md bg-paper-inset p-3">
                  {/* Promo Code Name */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex w-full items-center justify-between">
                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                          Promo Code Name
                        </Label>
                        {customPromos.length > 0 && (
                          <button
                            onClick={() => removePromo(index)}
                            className="pb-1 pr-0 text-ink-muted hover:text-ink-secondary"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 6L6 18" />
                              <path d="M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <Input
                      type="text"
                      value={promo.codeName}
                      data-index={index}
                      onChange={handlePromoNameChange}
                      onFocus={() => handleFieldFocus(`form_configuration-promo-codeName-${index}`)}
                      className={`h-7 ${
                        isFieldInvalid(`form_configuration-promo-codeName-${index}`)
                          ? 'border-red-500'
                          : ''
                      }`}
                      maxLength={50}
                    />
                  </div>

                  {/* Promo Code Type */}
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Promo Code Type
                    </Label>
                    <div className="flex space-x-4 pt-2">
                      <button
                        type="button"
                        className="flex flex-1 items-center gap-1"
                        onClick={() => handlePromoTypeChange(index, 'PERCENTAGE')}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                            promo.codeType === 'PERCENTAGE' ? 'border-sage' : 'border-edge'
                          }`}
                          aria-hidden
                        >
                          {promo.codeType === 'PERCENTAGE' && (
                            <span className="h-2 w-2 rounded-full bg-sage" />
                          )}
                        </span>
                        <span className="text-sm text-ink-secondary">Percentage</span>
                      </button>
                      <button
                        type="button"
                        className="flex flex-1 items-center gap-1"
                        onClick={() => handlePromoTypeChange(index, 'AMOUNT')}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                            promo.codeType === 'AMOUNT' ? 'border-sage' : 'border-edge'
                          }`}
                          aria-hidden
                        >
                          {promo.codeType === 'AMOUNT' && (
                            <span className="h-2 w-2 rounded-full bg-sage" />
                          )}
                        </span>
                        <span className="text-sm text-ink-secondary">Amount</span>
                      </button>
                    </div>
                  </div>

                  {/* Promo Amount */}
                  <div>
                    {promo.codeType === 'AMOUNT' ? (
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                            Discount ($)
                          </Label>
                          <div className="relative">
                            {/* Left $ label */}
                            <span className="absolute left-[10px] top-1/2 -translate-y-1/2 text-sm leading-none text-ink-muted">
                              $
                            </span>

                            {/* Input field */}
                            <input
                              type="text"
                              value={promo.codeAmount || ''}
                              data-index={index}
                              onChange={handlePromoAmountChange}
                              onFocus={() =>
                                handleFieldFocus(`form_configuration-promo-codeAmount-${index}`)
                              }
                              className={`block h-7 w-full rounded-md bg-white border border-edge-input pl-5 pr-12 text-sm leading-none focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage ${
                                isFieldInvalid(`form_configuration-promo-codeAmount-${index}`)
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                  : ''
                              }`}
                              placeholder="0.00"
                            />

                            {/* Right USD label */}
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-muted">
                              USD
                            </span>
                          </div>
                          {/* Helper text showing max discount - only show when not in invalid state and has tickets selected */}
                          {(() => {
                            const minPrice = getMinimumTicketPrice(promo);
                            const isInvalid = isFieldInvalid(
                              `form_configuration-promo-codeAmount-${index}`,
                            );
                            const shouldShowHelper = isInvalid;

                            if (shouldShowHelper) {
                              return (
                                <p className="mt-1 text-xs text-red-500">
                                  Max: ${minPrice.toFixed(2)}
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    ) : (
                      <>
                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                          Discount (%)
                        </Label>
                        <Input
                          id={`form_configuration-promo-codeAmount-${index}`}
                          name={`form_configuration-promo-codeAmount-${index}`}
                          type="number"
                          min="0"
                          max="100"
                          defaultValue={'0'}
                          value={promo.codeAmount || ''}
                          data-index={index}
                          onChange={handlePromoAmountChange}
                          onFocus={() =>
                            handleFieldFocus(`form_configuration-promo-codeAmount-${index}`)
                          }
                          className={`h-7 ${
                            isFieldInvalid(`form_configuration-promo-codeAmount-${index}`)
                              ? 'border-red-500'
                              : ''
                          }`}
                          placeholder="0"
                        />
                      </>
                    )}
                  </div>

                  {/* Applied Tickets */}
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Applied Tickets
                    </Label>
                    <div className="pt-2">
                      <div
                        className={`rounded-md`}
                        onFocus={() =>
                          handleFieldFocus(`form_configuration-promo-tickets-${index}`)
                        }
                      >
                        <StyledMultiSelectDropdown
                          options={customTickets
                            .filter((ticket) => parseFloat(ticket.price) > 0)
                            .map((ticket) => ({
                              value: ticket.name,
                              label: ticket.name,
                            }))}
                          setItems={(items) => handlePromoTicketsChange(items, index)}
                          items={promo.tickets2 ? promo.tickets2.map((item) => item.name) : []}
                          placeholder="Select ticket(s)"
                          className={
                            isFieldInvalid(`form_configuration-promo-tickets-${index}`)
                              ? 'border-red-500'
                              : ''
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add New Promo Button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={addNewPromo}
                className="flex items-center space-x-1 text-ink-secondary hover:text-ink"
              >
                <PlusIcon className="h-4 w-4" />
                <span className="text-sm">Add promo code</span>
              </button>
            </div>
          </div>
        ),
      },
      {
        key: FORM_STEPS.CHECKOUT,
        title: 'Checkout Details',
        content: (
          <div className="space-y-5">
            {/* Donor Information */}
            <div className="space-y-3">
              <Label className="text-base font-medium text-ink-secondary">Donor Information</Label>
              <div className="space-y-3">
                {/* Enable Anonymous Name */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={showAnonymousDonations}
                    onCheckedChange={(value) => handleInputChange('showAnonymousDonations', value)}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Enable Anonymous Name
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Let donors hide their name from public-facing displays.
                    </p>
                  </div>
                </div>

                {/* Enable Organization Name */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={showOrganizationNameField}
                    onCheckedChange={(value) =>
                      handleInputChange('showOrganizationNameField', value)
                    }
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Enable Organization Name
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Collect an optional company or organization name.
                    </p>
                  </div>
                </div>

                {/* Enable Phone Number */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={enablePhoneNumber}
                    onCheckedChange={(value) => handleInputChange('enablePhoneNumber', value)}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Enable Phone Number
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Ask attendees for a phone number at checkout.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Payment Settings */}
            <div className="space-y-3">
              <Label className="text-base font-medium text-ink-secondary">Payment Settings</Label>
              <div className="space-y-3">
                {/* Enable Billing Address */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enableBillingAddress}
                      onCheckedChange={(value) => handleInputChange('enableBillingAddress', value)}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Enable Billing Address
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Collect a billing address during checkout.
                      </p>
                    </div>
                  </div>

                  {enableBillingAddress && (
                    <div className="space-y-2 rounded-md border border-edge bg-paper-inset p-2">
                      <div className="space-y-1">
                        <Label
                          htmlFor="default-country"
                          className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                        >
                          Default Country
                        </Label>
                        <FormSelect
                          id="default-country"
                          value={selectedCountry}
                          onChange={(e) => handleInputChange('selectedCountry', e.target.value)}
                          className="h-7 leading-7"
                        >
                          <option value="">Select a country</option>
                          {selectedCountry && !countryLabels.has(selectedCountry) && (
                            <option value={selectedCountry}>{selectedCountry}</option>
                          )}
                          {countries.map((country) => (
                            <option key={country.value} value={country.label}>
                              {country.label}
                            </option>
                          ))}
                        </FormSelect>
                      </div>
                    </div>
                  )}
                </div>

                {/* Disable Checkout Bump */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={disableCheckoutBump}
                    onCheckedChange={(value) => handleInputChange('disableCheckoutBump', value)}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Disable Checkout Bump
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Hides the &ldquo;increase your impact&rdquo; upsell at checkout for this
                      event.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Additional Features */}
            <div className="space-y-3">
              <Label className="text-base font-medium text-ink-secondary">
                Additional Features
              </Label>
              <div className="space-y-3">
                {/* Enable Communication Consent */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enableCommunicationConsent}
                      onCheckedChange={(value) =>
                        handleInputChange('enableCommunicationConsent', value)
                      }
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Enable Communication Consent
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Show a marketing communication opt-in checkbox.
                      </p>
                    </div>
                  </div>

                  {enableCommunicationConsent && (
                    <div className="space-y-2 rounded-md border border-edge bg-paper-inset p-2">
                      <div className="space-y-1">
                        <Label
                          htmlFor="communication-consent-message"
                          className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                        >
                          Custom Message (Optional)
                        </Label>
                        <FormTextarea
                          id="communication-consent-message"
                          value={communicationConsentMessage}
                          onChange={(e) =>
                            handleInputChange('communicationConsentMessage', e.target.value)
                          }
                          className="min-h-[60px] text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Field Mapping Section - ICNA Relief Salesforce */}
            {(merchantName === 'ICNA Relief USA' || merchantName === 'ICNA Relief USA Dev') &&
              setSalesforceFieldMapperOpen &&
              salesforceCampaignIDICNAValues !== undefined && (
                <div className="mr-4 mt-2 flex">
                  <button
                    type="button"
                    className="flex cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setSalesforceFieldMapperOpen(true);
                    }}
                  >
                    <PlusIcon className="mr-1 mt-0.5 h-5 w-5 text-ink-muted" aria-hidden="true" />
                    <div className="text-ink-muted text-md">
                      {salesforceCampaignIDICNAValues
                        ? 'Edit Campaign ID Mapping'
                        : 'Map Campaign ID'}
                    </div>
                  </button>
                </div>
              )}

            {/* Display ICNA Relief Salesforce Mapping */}
            {(merchantName === 'ICNA Relief USA' || merchantName === 'ICNA Relief USA Dev') &&
              salesforceCampaignIDICNAValues && (
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex border border-edge p-1 px-2 rounded-md w-fit flex-col gap-1 shadow-md">
                    <Label className="block font-inter text-md font-medium leading-5 text-ink-muted">
                      {formName || 'Event'}
                    </Label>
                    <div className="flex items-center gap-1">
                      <span className="block font-inter text-xs font-medium leading-5 text-ink-muted">
                        Salesforce Campaign ID
                      </span>
                      <span className="block font-inter text-xs font-medium leading-5 text-ink-muted">
                        :
                      </span>
                      <span className="block font-inter text-xs font-medium leading-5 text-ink-muted">
                        {salesforceCampaignIDICNAValues}
                      </span>
                    </div>
                    <div className="absolute top-0 right-0">
                      <button
                        type="button"
                        onClick={() => setSalesforceCampaignIDICNAValues?.('')}
                        className="hover:opacity-70"
                      >
                        <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                          <path
                            d="M4 4L12 12M4 12L12 4"
                            stroke="#3A3429"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </div>
        ),
      },
    ];

    return (
      <div className="space-y-4">
        {steps.map((step) => {
          const isCurrentStep = step.key === currentStep;
          const hasErrors = getStepValidationErrors(step.key);
          const workspaceStepPresentation = {
            [FORM_STEPS.APPEARANCE]: {
              icon: PaintBrushIcon,
              description: 'Set the event identity, dates, location, and visual treatment.',
            },
            [FORM_STEPS.TICKETS]: {
              icon: TicketIcon,
              description: 'Configure ticket types, capacity, pricing, and attendee groups.',
            },
            [FORM_STEPS.PROMO_CODES]: {
              icon: TagIcon,
              description: 'Add optional promotion codes and define their discount rules.',
            },
            [FORM_STEPS.CHECKOUT]: {
              icon: CreditCardIcon,
              description: 'Control attendee information and the options collected at checkout.',
            },
          }[step.key];

          const shouldRenderStepContent = workspacePresentation || isCurrentStep;
          const stepContent = shouldRenderStepContent ? (
            <div className="space-y-4">
              {step.content}

              {!workspacePresentation && (
                <div className="mt-6">
                  <ButtonGroup
                    currentStep={currentStep}
                    onBack={handleBack}
                    onContinue={handleContinue}
                    isLastStep={isLastStep()}
                  />
                </div>
              )}
            </div>
          ) : null;

          if (workspacePresentation) {
            return (
              <div key={step.key} className="scroll-mt-4">
                <WorkspaceSectionCard
                  title={step.title}
                  description={workspaceStepPresentation?.description}
                  icon={workspaceStepPresentation?.icon}
                  expanded
                  hasErrors={hasErrors}
                >
                  {stepContent}
                </WorkspaceSectionCard>
              </div>
            );
          }

          return (
            <div key={step.key} className="space-y-4">
              {/* Section Header */}
              <SectionHeader
                title={step.title}
                onClick={() => setCurrentStep(step.key)}
                hasErrors={hasErrors}
                className="mb-0"
              />

              {/* Step Content - Only show if current step */}
              {stepContent}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`w-full space-y-2 ${workspacePresentation ? '' : 'pr-2'}`}>
      {renderAllFormSteps()}
    </div>
  );
}

export default EventFormConfigurationTab;
